import React, { useState, useEffect, useCallback } from 'react';
import {
  ShieldOff,
  ShieldCheck,
  AlertTriangle,
  X,
  Loader2,
  RefreshCw,
  History,
  Store,
  Power,
  PowerOff,
  CheckCircle,
  XCircle,
  Clock,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import {
  HRStaffService,
  type AgentCampaignStatus,
  type AgentCampaign,
} from '../services/hr-staff.service';

interface SalesRepDeactivationManagerProps {
  agentId: string;
  agentName: string;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  onClose: () => void;
}

export default function SalesRepDeactivationManager({
  agentId,
  agentName,
  onError,
  onSuccess,
  onClose,
}: SalesRepDeactivationManagerProps) {
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [status, setStatus] = useState<AgentCampaignStatus | null>(null);
  const [campaigns, setCampaigns] = useState<AgentCampaign[]>([]);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    type: 'toggle_sales' | 'campaign_deactivate' | 'campaign_reactivate' | 'deactivate' | 'activate';
    campaignId?: string;
    campaignName?: string;
    newValue?: boolean;
  } | null>(null);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [statusResult, campaignsResult] = await Promise.all([
        HRStaffService.getAgentCampaignStatus(agentId),
        HRStaffService.getAgentActiveCampaigns(agentId),
      ]);
      setStatus(statusResult);
      setCampaigns(campaignsResult);
    } catch (error: any) {
      console.error('Error loading sales rep status:', error);
      onError(error.message || 'Failed to load sales rep status');
    } finally {
      setLoading(false);
    }
  }, [agentId, onError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAction = (action: typeof pendingAction) => {
    setPendingAction(action);
    setReason('');
    setNotes('');
    setShowReasonModal(true);
  };

  const executeAction = async () => {
    if (!pendingAction || !reason.trim()) {
      onError('Please provide a reason for this action');
      return;
    }

    try {
      setActionLoading(true);
      let result;

      switch (pendingAction.type) {
        case 'toggle_sales':
          result = await HRStaffService.toggleAgentSalesAbility(
            agentId,
            pendingAction.newValue!,
            reason,
            notes || undefined
          );
          break;
        case 'campaign_deactivate':
          result = await HRStaffService.deactivateAgentForCampaign(
            agentId,
            pendingAction.campaignId!,
            reason,
            notes || undefined
          );
          break;
        case 'campaign_reactivate':
          result = await HRStaffService.reactivateAgentForCampaign(
            agentId,
            pendingAction.campaignId!,
            notes || undefined
          );
          break;
        case 'deactivate':
          result = await HRStaffService.deactivateAgentWithReason(
            agentId,
            reason,
            notes || undefined
          );
          break;
        case 'activate':
          result = await HRStaffService.activateAgentWithReason(
            agentId,
            reason,
            notes || undefined
          );
          break;
      }

      onSuccess(result?.message || 'Action completed successfully');
      setShowReasonModal(false);
      setPendingAction(null);
      loadData();
    } catch (error: any) {
      console.error('Error executing action:', error);
      onError(error.message || 'Failed to execute action');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = () => {
    if (!status) return null;

    if (status.status === 'terminated') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium bg-red-100 text-red-800 rounded-full">
          <XCircle className="w-4 h-4" />
          Terminated
        </span>
      );
    }
    if (status.status === 'deactivated' || !status.isActive) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium bg-gray-100 text-gray-800 rounded-full">
          <PowerOff className="w-4 h-4" />
          Deactivated
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium bg-green-100 text-green-800 rounded-full">
        <CheckCircle className="w-4 h-4" />
        Active
      </span>
    );
  };

  const getActionTitle = () => {
    if (!pendingAction) return '';
    switch (pendingAction.type) {
      case 'toggle_sales':
        return pendingAction.newValue ? 'Enable Sales Ability' : 'Disable Sales Ability';
      case 'campaign_deactivate':
        return `Deactivate from ${pendingAction.campaignName}`;
      case 'campaign_reactivate':
        return `Reactivate for ${pendingAction.campaignName}`;
      case 'deactivate':
        return 'Deactivate Sales Rep';
      case 'activate':
        return 'Activate Sales Rep';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ShieldOff className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Manage Sales Rep Status</h2>
              <p className="text-sm text-gray-500">{agentName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : status ? (
            <>
              {/* Current Status */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-gray-900">Current Status</h3>
                  {getStatusBadge()}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Sales Ability</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className={`font-medium ${status.canSell ? 'text-green-600' : 'text-red-600'}`}>
                        {status.canSell ? 'Enabled' : 'Disabled'}
                      </span>
                      <button
                        onClick={() => handleAction({
                          type: 'toggle_sales',
                          newValue: !status.canSell,
                        })}
                        disabled={status.status === 'terminated'}
                        className={`px-3 py-1 text-sm font-medium rounded-lg transition-colors ${
                          status.canSell
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {status.canSell ? 'Disable' : 'Enable'}
                      </button>
                    </div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Account Status</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className={`font-medium ${status.isActive ? 'text-green-600' : 'text-red-600'}`}>
                        {status.isActive ? 'Active' : 'Deactivated'}
                      </span>
                      {status.status !== 'terminated' && (
                        <button
                          onClick={() => handleAction({
                            type: status.isActive ? 'deactivate' : 'activate',
                          })}
                          className={`px-3 py-1 text-sm font-medium rounded-lg transition-colors ${
                            status.isActive
                              ? 'bg-red-100 text-red-700 hover:bg-red-200'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                        >
                          {status.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Campaign-Specific Deactivation */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Store className="w-5 h-5 text-gray-400" />
                  <h3 className="font-medium text-gray-900">Campaign Access</h3>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  Deactivate the sales rep from specific campaigns while keeping their login active for other campaigns.
                </p>

                {campaigns.length > 0 ? (
                  <div className="space-y-2">
                    {campaigns.map((campaign) => (
                      <div
                        key={campaign.id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          campaign.isActive
                            ? 'bg-green-50 border-green-200'
                            : 'bg-red-50 border-red-200'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${campaign.isActive ? 'bg-green-100' : 'bg-red-100'}`}>
                            {campaign.isActive ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{campaign.name}</p>
                            <p className="text-sm text-gray-500">
                              {campaign.isActive ? 'Sales rep can work on this campaign' : 'Sales rep is deactivated for this campaign'}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleAction({
                            type: campaign.isActive ? 'campaign_deactivate' : 'campaign_reactivate',
                            campaignId: campaign.id,
                            campaignName: campaign.name,
                          })}
                          disabled={status.status === 'terminated'}
                          className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                            campaign.isActive
                              ? 'bg-red-100 text-red-700 hover:bg-red-200'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {campaign.isActive ? 'Deactivate' : 'Reactivate'}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-4">
                    No campaigns assigned to this sales rep's organization
                  </p>
                )}

                {/* Show current campaign deactivations */}
                {status.campaignDeactivations.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-sm font-medium text-gray-700 mb-2">Current Campaign Deactivations:</p>
                    <div className="space-y-2">
                      {status.campaignDeactivations.map((deactivation, index) => (
                        <div key={index} className="flex items-center justify-between text-sm p-2 bg-red-50 rounded-lg">
                          <div>
                            <span className="font-medium text-red-800">{deactivation.campaignName}</span>
                            <span className="text-red-600 ml-2">- {deactivation.reason}</span>
                          </div>
                          <span className="text-red-500 text-xs">
                            {new Date(deactivation.deactivatedAt).toLocaleDateString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Status History */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="flex items-center justify-between w-full"
                >
                  <div className="flex items-center gap-2">
                    <History className="w-5 h-5 text-gray-400" />
                    <h3 className="font-medium text-gray-900">Status History</h3>
                  </div>
                  {showHistory ? (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </button>

                {showHistory && status.statusHistory.length > 0 && (
                  <div className="mt-4 space-y-3">
                    {status.statusHistory.map((entry) => (
                      <div key={entry.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="p-1.5 bg-gray-200 rounded-full">
                          <Clock className="w-3 h-3 text-gray-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-gray-900 text-sm">{entry.action.replace(/_/g, ' ')}</p>
                            <span className="text-xs text-gray-500">
                              {new Date(entry.performedAt).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{entry.reason}</p>
                          {entry.notes && (
                            <p className="text-xs text-gray-400 mt-1">Note: {entry.notes}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {showHistory && status.statusHistory.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-4 mt-4">
                    No status history available
                  </p>
                )}
              </div>
            </>
          ) : (
            <p className="text-center text-gray-500 py-12">Failed to load sales rep status</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>

      {/* Reason Modal */}
      {showReasonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{getActionTitle()}</h3>
              <p className="text-sm text-gray-500 mt-1">
                Please provide a reason for this action. This will be recorded in the status history.
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Enter reason for this action..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional notes..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowReasonModal(false);
                  setPendingAction(null);
                }}
                disabled={actionLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={executeAction}
                disabled={actionLoading || !reason.trim()}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
